import React, { useState, useEffect } from "react";
import {
  X,
  Plane,
  Laptop,
  School,
  ChevronDown,
  AlertTriangle,
} from "lucide-react";
import {
  getEstimatedResultOptions,
  getLeadFields,
  getStudentResultOptions,
} from "../../formSchemas/leadSchemas";
import StatusBadge from "../../Components/StatusBadge";
import { auth, db, storage } from "../../firebase";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { normalizeRowKeys } from "../../utils/case";
import {
  convertToDateInput,
  convertToDisplayDate,
  toDdMmYyyy,
} from "../../utils/date";
import { showSuccessToast, showErrorToast } from "../../toastmsg";
import GroupField from "../../Components/Form/FormComponent/GroupField";
import countryCodeList from "../../assets/countryCode.json";
import type { AssigneeOption } from "../../hooks/useAssignees";
import SubmissionHistorySection from "../LeadSections/SubmissionHistorySection";
import CourseFinderSection from "../LeadSections/CourseFinderSection";
import CareerTestSection from "../LeadSections/CareerTestSection";
import UecRevisionClassSection from "../LeadSections/UecRevisionClassSection";
import DuplicateLeadDifferencesSection from "../LeadSections/DuplicateLeadDifferencesSection";
import ConfirmationDialog from "../ConfirmationDialogs/ConfirmationDialogs";
import SpmRevisionClassSection from "../LeadSections/SpmRevisionClassSection";

interface LeadFormProps {
  lead: Record<string, any>;
  userRole: string | null;
  currentUserDisplayName: string | null;
  authLoading: boolean;
  assigneeOptions: AssigneeOption[];
  onClose: () => void;
  onSave?: (updatedLead: Record<string, any>) => void;
}

// Field names as stored in Firestore for new entries (camelCase),
// but we will also fall back to legacy label-based keys when reading
const dateFieldNames = ["createdDate", "firstTouchedDate", "lastTouchedDate"];

const getBranchForAssignee = (assignee: unknown): string => {
  const normalizedAssignee = String(assignee || "")
    .trim()
    .toLowerCase();

  if (!normalizedAssignee) return "";
  return normalizedAssignee.includes("matthew") ||
    normalizedAssignee.includes("elmo")
    ? "Kuantan"
    : "KD";
};

// Controls which duplicate values can be copied into the current lead.
// Add or remove a Firestore field name here to enable or disable its merge button.
const MERGEABLE_DUPLICATE_FIELDS = new Set([
  "name",
  "ic",
  "dob",
  "gender",
  "country",
  "countryCode",
  "phoneNumber",
  "email",
  "leadSource",
  "campaign",
  "location",
  "preferredUniversity",
  "intake",
  "campusVisit",
  "videoCall",
  "familyFinancials",
  "qualification",
  "studentResult",
  "overseas",
  "interestedCourses",
  "schoolName",
  "reason",
  "remarks",
  "discoverySource",
  "tuition.subject",
]);

// These fields remain non-mergeable even if accidentally added above.
const PROTECTED_DUPLICATE_FIELDS = new Set([
  "createdDate",
  "firstTouchedDate",
  "lastTouchedDate",
  "firstTouchedAt",
  "lastTouchedAt",
  "uploadedResult",
  "studentUID",
  "status",
]);

const toIsoTouchedDate = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === "") return undefined;

  if (
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }

  const dateText = String(value).trim();
  const dateOnlyMatch = dateText.match(
    /^(\d{4})-(\d{2})-(\d{2})$|^(\d{2})-(\d{2})-(\d{4})$/,
  );

  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1] || dateOnlyMatch[6]);
    const month = Number(dateOnlyMatch[2] || dateOnlyMatch[5]);
    const day = Number(dateOnlyMatch[3] || dateOnlyMatch[4]);
    const parsedDate = new Date(Date.UTC(year, month - 1, day));

    if (
      parsedDate.getUTCFullYear() === year &&
      parsedDate.getUTCMonth() === month - 1 &&
      parsedDate.getUTCDate() === day
    ) {
      return parsedDate.toISOString();
    }
    return undefined;
  }

  const parsedDate = new Date(dateText);
  return Number.isNaN(parsedDate.getTime())
    ? undefined
    : parsedDate.toISOString();
};

const getTouchedDateUpdates = (
  existingData: Record<string, any>,
): Record<string, string> => {
  const touchedAt = new Date().toISOString();

  return {
    firstTouchedDate:
      toIsoTouchedDate(existingData.firstTouchedDate) || touchedAt,
    lastTouchedDate: touchedAt,
    firstTouchedAt: toIsoTouchedDate(existingData.firstTouchedAt) || touchedAt,
    lastTouchedAt: touchedAt,
  };
};

const RIASEC_LABELS: Record<string, string> = {
  R: "Realistic",
  I: "Investigative",
  A: "Artistic",
  S: "Social",
  E: "Enterprising",
  C: "Conventional",
};

const RIASEC_RECOMMENDATIONS: Record<
  string,
  { fields: string[]; careers: string[]; courses: string[] }
> = {
  R: {
    fields: [
      "Engineering",
      "Architecture",
      "Veterinary Science",
      "Agriculture",
      "Construction Management",
    ],
    careers: [
      "Civil Engineer",
      "Mechanical Engineer",
      "Architect",
      "Pilot",
      "Quantity Surveyor",
    ],
    courses: [
      "BEng Civil Engineering",
      "BEng Mechanical Engineering",
      "Bachelor of Architecture",
      "Diploma in Electrical Technology",
    ],
  },
  I: {
    fields: [
      "Medicine",
      "Biotechnology",
      "Computer Science",
      "Data Science",
      "Pharmacy",
      "Environmental Science",
    ],
    careers: [
      "Medical Doctor",
      "Researcher",
      "Data Scientist",
      "Pharmacist",
      "Software Engineer",
      "Biochemist",
    ],
    courses: [
      "MBBS Medicine",
      "BSc Biotechnology",
      "BSc Computer Science",
      "BSc Pharmacy",
      "BSc Data Analytics",
    ],
  },
  A: {
    fields: [
      "Graphic Design",
      "Interior Design",
      "Mass Communication",
      "Film & Media",
      "Fine Arts",
      "Animation",
    ],
    careers: [
      "Graphic Designer",
      "UX/UI Designer",
      "Filmmaker",
      "Journalist",
      "Animator",
      "Fashion Designer",
    ],
    courses: [
      "BA Graphic Design",
      "BA Mass Communication",
      "BA Interior Design",
      "BA Animation & Digital Media",
    ],
  },
  S: {
    fields: [
      "Education",
      "Psychology",
      "Counselling",
      "Nursing",
      "Social Work",
      "Public Health",
    ],
    careers: [
      "Teacher",
      "Psychologist",
      "Counsellor",
      "Nurse",
      "HR Manager",
      "Social Worker",
    ],
    courses: [
      "B.Ed. TESL",
      "BA Psychology",
      "BSc Nursing",
      "BA Social Work",
      "BBA Human Resource Management",
    ],
  },
  E: {
    fields: [
      "Business Management",
      "Marketing",
      "Law",
      "Entrepreneurship",
      "International Business",
      "Event Management",
    ],
    careers: [
      "Business Manager",
      "Marketing Executive",
      "Lawyer",
      "Entrepreneur",
      "Sales Director",
      "Event Planner",
    ],
    courses: [
      "BBA Business Management",
      "BA Marketing",
      "LLB Law",
      "BBA International Business",
      "Diploma Hotel Management",
    ],
  },
  C: {
    fields: [
      "Accounting",
      "Finance",
      "Actuarial Science",
      "Information Systems",
      "Banking",
      "Economics",
    ],
    careers: [
      "Accountant",
      "Auditor",
      "Financial Analyst",
      "Actuary",
      "Bank Officer",
      "Economist",
    ],
    courses: [
      "B.Acc Accounting",
      "BSc Actuarial Science",
      "BSc Finance",
      "BSc Information Systems",
      "BA Economics",
    ],
  },
};

const formatList = (value: unknown): string => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  if (typeof value === "string") return value;
  return "";
};

const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  if (Array.isArray(value)) return formatList(value) || "-";
  if (
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    const dateValue = (value as { toDate: () => Date }).toDate();
    return toDdMmYyyy(dateValue) || String(dateValue);
  }
  if (value instanceof Date) return toDdMmYyyy(value) || value.toISOString();
  return String(value);
};

const toListItems = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const BulletList = ({
  items,
  itemKey,
}: {
  items: unknown;
  itemKey: string;
}) => {
  const listItems = toListItems(items);

  if (!listItems.length) return <p>-</p>;

  return (
    <ul className="grid grid-cols-2 mt-1 list-disc space-y-1 pl-5">
      {listItems.map((item, index) => (
        <li key={`${itemKey}-${item}-${index}`}>{item}</li>
      ))}
    </ul>
  );
};

const formatPhone = (countryCode?: string, phoneNumber?: string): string => {
  const code = countryCode || "";
  const phone = phoneNumber || "";
  if (!code && !phone) return "-";
  return `${code} ${phone}`.trim();
};

const formatMatchField = (field: string, lead: Record<string, any>): string => {
  if (field === "phoneNumber") {
    return `Phone number (${formatPhone(lead.countryCode, lead.phoneNumber)})`;
  }
  if (field === "email") return `Email (${lead.email || "-"})`;
  if (field === "ic") return `IC (${lead.ic || "-"})`;
  return field;
};

const uniqueFromTopTypes = (
  topTypes: string[] | undefined,
  key: "fields" | "careers" | "courses",
): string[] => {
  if (!topTypes?.length) return [];
  return [
    ...new Set(
      topTypes.flatMap((type) => RIASEC_RECOMMENDATIONS[type]?.[key] || []),
    ),
  ];
};

const getCareerRecommendations = (careerTestResult: any) => ({
  fields:
    careerTestResult?.recommendations?.fields ||
    uniqueFromTopTypes(careerTestResult?.topTypes, "fields"),
  courses:
    careerTestResult?.recommendations?.courses ||
    uniqueFromTopTypes(careerTestResult?.topTypes, "courses"),
  careers:
    careerTestResult?.recommendations?.careers ||
    uniqueFromTopTypes(careerTestResult?.topTypes, "careers"),
});

interface LeadSubmissionHistoryItem {
  id: string;
  submissionType?: string;
  submittedAt?: unknown;
  mergedAt?: unknown;
  updatedAt?: unknown;
  status?: string;
  mergeStatus?: string;
  mergeFields?: unknown;
  requestedChanges?: unknown;
  appliedChanges?: unknown;
  changedFields?: unknown;
  changedFieldNames?: unknown;
  changeCount?: unknown;
}

interface SubmissionChangeEntry {
  field: string;
  requestedBefore?: unknown;
  requestedAfter?: unknown;
  appliedBefore?: unknown;
  appliedAfter?: unknown;
  hasRequestedChange: boolean;
  hasAppliedChange: boolean;
}

const toDateMillis = (value: unknown): number => {
  if (
    value &&
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate().getTime();
  }

  const milliseconds = new Date(String(value || "")).getTime();
  return Number.isNaN(milliseconds) ? 0 : milliseconds;
};

const isRecord = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const flattenLeadValues = (
  value: Record<string, any>,
  prefix = "",
): Record<string, unknown> =>
  Object.entries(value).reduce<Record<string, unknown>>(
    (result, [key, nestedValue]) => {
      const field = prefix ? `${prefix}.${key}` : key;
      const isFirestoreTimestamp =
        isRecord(nestedValue) && typeof nestedValue.toDate === "function";

      if (isRecord(nestedValue) && !isFirestoreTimestamp) {
        Object.assign(result, flattenLeadValues(nestedValue, field));
      } else {
        result[field] = nestedValue;
      }

      return result;
    },
    {},
  );

const comparisonValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) {
    return JSON.stringify(value.map(comparisonValue).sort());
  }
  if (
    typeof value === "object" &&
    value !== null &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  if (value instanceof Date) return value.toISOString();
  return String(value);
};

const hasValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.some(hasValue);
  return true;
};

const getLeadDifferences = (
  currentLead: Record<string, any>,
  duplicateLead: Record<string, any>,
) => {
  const currentValues = flattenLeadValues(currentLead);
  const duplicateValues = flattenLeadValues(duplicateLead);
  const ignoredFields = new Set([
    "id",
    "ID",
    "studentUID",
    "email",
    "updatedAt",
    "duplicateOf",
    "dedupState",
    "isMaster",
    "portalPasswordSetup",
    "portalProvisionedAt",
    "portalProvisioningStatus",
    "tuition.updatedAt",
    "accountStatus",
    "createdAt",
    "createdDate",
    "firstTouchedAt",
    "firstTouchedDate",
    "lastTouchedAt",
    "lastTouchedDate",
    "profileCompletion",
    "status",
  ]);

  return [
    ...new Set([
      ...Object.keys(currentValues),
      ...Object.keys(duplicateValues),
    ]),
  ]
    .filter(
      (field) =>
        !ignoredFields.has(field) &&
        field !== "courseFinderResult" &&
        !field.startsWith("courseFinderResult."),
    )
    .filter(
      (field) =>
        hasValue(duplicateValues[field]) &&
        comparisonValue(currentValues[field]) !==
          comparisonValue(duplicateValues[field]),
    )
    .sort()
    .map((field) => ({
      field,
      currentValue: currentValues[field],
      duplicateValue: duplicateValues[field],
    }));
};

const copyMergeValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return [...value];
  if (isRecord(value)) return { ...value };
  return value;
};

const setValueAtPath = (
  source: Record<string, any>,
  path: string,
  value: unknown,
): Record<string, any> => {
  const keys = path.split(".");
  const result = { ...source };
  let target = result;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      target[key] = copyMergeValue(value);
      return;
    }

    target[key] = isRecord(target[key]) ? { ...target[key] } : {};
    target = target[key];
  });

  return result;
};

const formatFieldName = (field: string): string => {
  const fieldLabels: Record<string, string> = {
    chineseName: "Chinese Name",
    name: "English Name",
    schoolName: "School Name",
    identity: "Identity",
    country: "Country",
    countryCode: "Country Code",
    phoneNumber: "Phone Number",
    email: "Email",
    discoverySource: "Discovery Source",
    "tuition.qualification": "Qualification",
    "tuition.subject": "Subjects",
    tuition: "Tuition Details",
    leadSource: "Lead Source",
    sourceDetail: "Source Detail",
    campaign: "Campaign",
    createdDate: "Created Date",
  };

  if (fieldLabels[field]) return fieldLabels[field];

  return field
    .split(".")
    .map((part) =>
      part
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/^./, (character) => character.toUpperCase()),
    )
    .join(" / ");
};

const formatAuditValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "Not set";
  if (Array.isArray(value)) {
    return value.length ? value.map(formatAuditValue).join(", ") : "None";
  }
  if (
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    return convertToDisplayDate(value) || "Not set";
  }
  if (value instanceof Date) return convertToDisplayDate(value) || "Not set";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (isRecord(value)) {
    const formattedEntries = Object.entries(value).map(
      ([key, nestedValue]) =>
        `${formatFieldName(key)}: ${formatAuditValue(nestedValue)}`,
    );
    return formattedEntries.length ? formattedEntries.join("; ") : "None";
  }
  return String(value);
};

const getChangeValues = (
  value: unknown,
  changeType: "requested" | "applied",
): { before?: unknown; after?: unknown; exists: boolean } => {
  if (!isRecord(value)) return { exists: false };

  const beforeKeys = ["previous", "before", "old", "oldValue"];
  const afterKeys =
    changeType === "requested"
      ? ["submitted", "after", "new", "newValue"]
      : ["after", "submitted", "new", "newValue"];
  const beforeKey = beforeKeys.find((key) => key in value);
  const afterKey = afterKeys.find((key) => key in value);

  return {
    before: beforeKey ? value[beforeKey] : undefined,
    after: afterKey ? value[afterKey] : undefined,
    exists: Boolean(beforeKey || afterKey),
  };
};

const toChangeMap = (value: unknown): Record<string, unknown> => {
  if (isRecord(value)) return value;
  if (!Array.isArray(value)) return {};

  return value.reduce<Record<string, unknown>>((result, item) => {
    if (isRecord(item) && typeof item.field === "string") {
      result[item.field] = item;
    }
    return result;
  }, {});
};

const getSubmissionChangeEntries = (
  submission: LeadSubmissionHistoryItem,
): SubmissionChangeEntry[] => {
  // `changedFields` supports documents written during the audit-field rollout.
  const requestedMap = toChangeMap(
    submission.requestedChanges || submission.changedFields,
  );
  const storedAppliedMap = toChangeMap(submission.appliedChanges);
  const workflowStatus = submission.status || submission.mergeStatus;
  const hasStoredAppliedChanges = Object.keys(storedAppliedMap).length > 0;
  // Compact merged documents omit appliedChanges when the requested values
  // were applied exactly as submitted.
  const appliedMap =
    workflowStatus === "merged" && !hasStoredAppliedChanges
      ? requestedMap
      : storedAppliedMap;
  const namedFields = Array.isArray(submission.changedFieldNames)
    ? submission.changedFieldNames.map(String)
    : [];
  const fields = [
    ...new Set([
      ...namedFields,
      ...Object.keys(requestedMap),
      ...Object.keys(appliedMap),
    ]),
  ];

  return fields.map((field) => {
    const requested = getChangeValues(requestedMap[field], "requested");
    const applied = getChangeValues(appliedMap[field], "applied");
    return {
      field,
      requestedBefore: requested.before,
      requestedAfter: requested.after,
      appliedBefore: applied.before,
      appliedAfter: applied.after,
      hasRequestedChange: requested.exists,
      hasAppliedChange: applied.exists,
    };
  });
};

const formatUpdatedFields = (value: unknown): string => {
  if (!Array.isArray(value) || value.length === 0) return "Not available";

  return value.map((field) => formatFieldName(String(field))).join(", ");
};

const formatSubmissionType = (submissionType?: string): string => {
  const submissionTypeLabels: Record<string, string> = {
    "manual-add": "Add Lead",
    "bulk-upload": "Upload Leads",
    "uec2026-registration": "UEC2026 Landing Page",
  };

  if (!submissionType) return "Legacy / Unknown Source";
  if (submissionTypeLabels[submissionType]) {
    return submissionTypeLabels[submissionType];
  }

  return submissionType
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const SubmissionHistoryCard = ({
  submission,
  position,
}: {
  submission: LeadSubmissionHistoryItem;
  position: number;
}) => {
  const hasAuditMetadata =
    submission.requestedChanges !== undefined ||
    submission.appliedChanges !== undefined ||
    submission.changedFields !== undefined ||
    submission.changedFieldNames !== undefined ||
    submission.changeCount !== undefined;
  const submissionDate =
    submission.submittedAt || submission.mergedAt || submission.updatedAt;
  // Prefer the compact schema's status while retaining legacy display support.
  const workflowStatus = submission.status || submission.mergeStatus || "-";
  const submissionSource = formatSubmissionType(submission.submissionType);
  const showAppliedChanges = workflowStatus === "merged";
  const changes = getSubmissionChangeEntries(submission).filter((change) => {
    const hasNoDetailedValues =
      !change.hasRequestedChange && !change.hasAppliedChange;
    return showAppliedChanges
      ? change.hasAppliedChange || hasNoDetailedValues
      : change.hasRequestedChange || hasNoDetailedValues;
  });
  const changeCount = changes.length;

  return (
    <article className="rounded-md border border-violet-200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-violet-950">
            Submission {position + 1}
          </p>
          <p className="text-xs text-violet-700">
            {convertToDisplayDate(submissionDate) || "Date not available"}
          </p>
          <p className="mt-1 text-xs font-medium text-slate-700">
            Source: {submissionSource}
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800">
            {workflowStatus}
          </span>
          {hasAuditMetadata && (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              {changeCount} {changeCount === 1 ? "change" : "changes"}
            </span>
          )}
        </div>
      </div>

      {hasAuditMetadata ? (
        changes.length === 0 ? (
          <p className="mt-3 rounded bg-gray-50 p-2 text-xs text-gray-600">
            {showAppliedChanges
              ? "No lead profile fields were applied by this submission."
              : "No lead profile field changes were requested."}
          </p>
        ) : (
          <div className="mt-3 space-y-2">
            {changes.map((change) => (
              <div
                key={change.field}
                className="rounded-md border border-gray-200 p-2 text-xs text-gray-700"
              >
                <p className="mb-2 font-semibold text-gray-900">
                  {formatFieldName(change.field)}
                </p>

                {!showAppliedChanges && change.hasRequestedChange && (
                  <div className="grid grid-cols-[76px_1fr] gap-x-2 gap-y-1">
                    <span className="font-medium text-amber-700">
                      Requested
                    </span>
                    <div className="min-w-0">
                      <p className="break-words">
                        <span className="text-gray-500">Previous: </span>
                        {formatAuditValue(change.requestedBefore)}
                      </p>
                      <p className="break-words">
                        <span className="text-gray-500">Submitted: </span>
                        {formatAuditValue(change.requestedAfter)}
                      </p>
                    </div>
                  </div>
                )}

                {showAppliedChanges && change.hasAppliedChange && (
                  <div className="grid grid-cols-[76px_1fr] gap-x-2 gap-y-1">
                    <span className="font-medium text-emerald-700">
                      Applied
                    </span>
                    <div className="min-w-0">
                      <p className="break-words">
                        <span className="text-gray-500">Before: </span>
                        {formatAuditValue(change.appliedBefore)}
                      </p>
                      <p className="break-words">
                        <span className="text-gray-500">After: </span>
                        {formatAuditValue(change.appliedAfter)}
                      </p>
                    </div>
                  </div>
                )}

                {!change.hasRequestedChange && !change.hasAppliedChange && (
                  <p className="text-gray-500">
                    This field was marked as changed, but detailed values are
                    unavailable.
                  </p>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="mt-3 rounded bg-gray-50 p-2 text-xs text-gray-600">
          <p className="font-medium text-gray-700">Legacy submission</p>
          <p className="mt-1">
            <span className="font-medium">Fields updated: </span>
            {formatUpdatedFields(submission.mergeFields)}
          </p>
          <p className="mt-1">
            Before-and-after values were not recorded for this older submission.
          </p>
        </div>
      )}
    </article>
  );
};

const UECSideLeadForm: React.FC<LeadFormProps> = ({
  lead,
  userRole,
  currentUserDisplayName,
  authLoading,
  assigneeOptions,
  onClose,
  onSave,
}) => {
  const leadId = lead?.ID || lead?.id;
  const [editableLead, setEditableLead] = useState<Record<string, any>>({});
  const [showRemarks, setShowRemarks] = useState(true);
  const [showDetails, setShowDetails] = useState(true);
  const [showCourseFinder, setShowCourseFinder] = useState(false);
  const [showDuplicateDifferences, setShowDuplicateDifferences] =
    useState(false);
  const [showSubmissionHistory, setShowSubmissionHistory] = useState(false);
  const [showCareerTest, setShowCareerTest] = useState(false);
  const [showUecRevisionClass, setShowUecRevisionClass] = useState(false);
  const [isRemarkEditing, setisRemarkEditing] = useState(false);
  const [isDetailsEditing, setisDetailsEditing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [initialLead, setInitialLead] = useState<Record<string, any>>({});
  const [fields, setFields] = useState<any[]>([]);
  const [uploadedResultURL, setUploadedResultURL] = useState<string | null>(
    null,
  );
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submissionHistory, setSubmissionHistory] = useState<
    LeadSubmissionHistoryItem[]
  >([]);
  const [isSubmissionHistoryLoading, setIsSubmissionHistoryLoading] =
    useState(false);
  const [submissionHistoryError, setSubmissionHistoryError] = useState("");
  const [duplicateLeads, setDuplicateLeads] = useState<Record<string, any>[]>(
    [],
  );
  const [isDuplicateLeadsLoading, setIsDuplicateLeadsLoading] = useState(false);
  const [duplicateLeadsError, setDuplicateLeadsError] = useState("");
  const [pendingDuplicateUpdate, setPendingDuplicateUpdate] = useState<{
    field: string;
    value: unknown;
  } | null>(null);

  useEffect(() => {
    setDuplicateLeads([]);
    setDuplicateLeadsError("");
    setIsDuplicateLeadsLoading(false);
  }, [leadId, lead?.studentUID, lead?.email]);

  useEffect(() => {
    setSubmissionHistory([]);
    setSubmissionHistoryError("");
    setIsSubmissionHistoryLoading(false);
  }, [leadId]);

  const cancelEditing = (
    event: React.MouseEvent<HTMLButtonElement>,
    setEditing: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    event.stopPropagation();
    setEditing(false);
    setEditableLead(initialLead);
    setErrors({});
  };

  const canMergeField = (fieldPath: string) => {
    const rootField = fieldPath.split(".")[0];
    if (PROTECTED_DUPLICATE_FIELDS.has(rootField)) return false;
    return MERGEABLE_DUPLICATE_FIELDS.has(fieldPath);
  };

  const useDuplicateValue = (fieldPath: string, value: unknown) => {
    setPendingDuplicateUpdate({ field: fieldPath, value });
  };

  const confirmDuplicateUpdate = async () => {
    if (!pendingDuplicateUpdate) return;

    const { field, value } = pendingDuplicateUpdate;
    setPendingDuplicateUpdate(null);

    try {
      if (!leadId) throw new Error("Lead ID is missing");

      await updateDoc(doc(db, "leads", leadId), { [field]: value });

      setInitialLead((current) => setValueAtPath(current, field, value));
      setEditableLead((current) => setValueAtPath(current, field, value));
      showSuccessToast(`${formatFieldName(field)} updated successfully`);
    } catch (error: any) {
      console.error("Failed to use duplicate value:", error);
      showErrorToast(error.message || "Failed to use duplicate value.");
    }
  };

  useEffect(() => {
    if (
      !showDuplicateDifferences ||
      !leadId ||
      authLoading ||
      !auth.currentUser
    ) {
      setIsDuplicateLeadsLoading(false);
      return;
    }

    setIsDuplicateLeadsLoading(true);
    setDuplicateLeadsError("");

    const duplicateQuery = query(
      collection(db, "leads"),
      where("duplicateOf", "==", leadId),
      where("status", "==", "Duplicate"),
    );

    const unsubscribe = onSnapshot(
      duplicateQuery,
      (snapshot) => {
        const matches = snapshot.docs
          .map((duplicateDoc) => ({
            ID: duplicateDoc.id,
            id: duplicateDoc.id,
            ...duplicateDoc.data(),
          }))
          .filter((duplicateLead) => duplicateLead.ID !== leadId)
          .sort(
            (left, right) =>
              (toDateMillis(right.createdAt) ||
                toDateMillis(right.createdDate)) -
              (toDateMillis(left.createdAt) || toDateMillis(left.createdDate)),
          );

        setDuplicateLeads(matches);
        setDuplicateLeadsError("");
        setIsDuplicateLeadsLoading(false);
      },
      (error) => {
        console.error("Failed to load duplicate leads:", error);
        setDuplicateLeadsError("Unable to load duplicate lead differences.");
        setIsDuplicateLeadsLoading(false);
      },
    );

    return unsubscribe;
  }, [showDuplicateDifferences, leadId, authLoading]);

  useEffect(() => {
    if (!showSubmissionHistory || !leadId || authLoading || !auth.currentUser) {
      setIsSubmissionHistoryLoading(false);
      return;
    }

    setIsSubmissionHistoryLoading(true);
    setSubmissionHistoryError("");

    const canonicalHistoryQuery = query(
      collection(db, "lead-submissions"),
      where("canonicalLeadId", "==", leadId),
    );
    const comparisonHistoryQuery = query(
      collection(db, "lead-submissions"),
      where("comparisonLeadId", "==", leadId),
    );
    let canonicalHistory: LeadSubmissionHistoryItem[] = [];
    let comparisonHistory: LeadSubmissionHistoryItem[] = [];
    let canonicalLoaded = false;
    let comparisonLoaded = false;

    const publishHistory = () => {
      const historyById = new Map<string, LeadSubmissionHistoryItem>();
      [...canonicalHistory, ...comparisonHistory].forEach((submission) => {
        historyById.set(submission.id, submission);
      });
      const history = [...historyById.values()].sort(
        (left, right) =>
          toDateMillis(right.submittedAt || right.mergedAt || right.updatedAt) -
          toDateMillis(left.submittedAt || left.mergedAt || left.updatedAt),
      );

      setSubmissionHistory(history);
      setSubmissionHistoryError("");
      if (canonicalLoaded && comparisonLoaded) {
        setIsSubmissionHistoryLoading(false);
      }
    };

    const mapSnapshot = (snapshot: {
      docs: Array<{
        id: string;
        data: () => Record<string, any>;
      }>;
    }): LeadSubmissionHistoryItem[] =>
      snapshot.docs.map((submissionDoc) => ({
        id: submissionDoc.id,
        ...submissionDoc.data(),
      }));

    const handleHistoryError = (error: Error) => {
      console.error("Failed to load lead submission history:", error);
      setSubmissionHistoryError("Unable to load complete submission history.");
      setIsSubmissionHistoryLoading(false);
    };

    const unsubscribeCanonical = onSnapshot(
      canonicalHistoryQuery,
      (snapshot) => {
        canonicalHistory = mapSnapshot(snapshot);
        canonicalLoaded = true;
        publishHistory();
      },
      handleHistoryError,
    );
    const unsubscribeComparison = onSnapshot(
      comparisonHistoryQuery,
      (snapshot) => {
        comparisonHistory = mapSnapshot(snapshot);
        comparisonLoaded = true;
        publishHistory();
      },
      handleHistoryError,
    );

    return () => {
      unsubscribeCanonical();
      unsubscribeComparison();
    };
  }, [showSubmissionHistory, leadId, authLoading]);

  // Update fields when assignee options change
  useEffect(() => {
    const optionsData = assigneeOptions.map((opt) => ({
      uid: opt.id,
      displayName: opt.displayName,
    }));

    const leadFields = getLeadFields(optionsData.map((u) => u.displayName));
    leadFields.forEach((f) => {
      if (f.name === "assignee") {
        f.optionsData = optionsData;
        if (optionsData.length === 0 && lead?.assignee) {
          f.optionsData = [
            { uid: "fallback-assignee", displayName: lead.assignee },
          ];
        }
      }
      if (f.name === "estimatedResult") {
        f.options = getEstimatedResultOptions(lead?.qualification);
      }
      if (f.name === "studentResult") {
        f.options = getStudentResultOptions(lead?.qualification);
      }
    });

    setFields(leadFields);
  }, [assigneeOptions, lead?.assignee]);

  // Map lead to editable state
  useEffect(() => {
    if (isDetailsEditing) return;
    if (lead && fields.length > 0) {
      const normalizedLead = {
        ...lead,
        ...normalizeRowKeys(lead),
      } as Record<string, any>;

      const mapped = fields.reduce(
        (acc, field) => {
          const fieldName = field.name;

          // Skip file type fields - they cannot have values set programmatically
          if (field.type === "file") {
            return acc;
          }

          let val =
            normalizedLead[fieldName] ??
            lead[fieldName] ??
            lead[field.label] ??
            "";

          if (dateFieldNames.includes(fieldName)) {
            val = convertToDateInput(String(val));
          }

          if (fieldName === "assignee") {
            acc.assignee = val || "";
            const match = field.optionsData?.find((o) => o.displayName === val);
            acc.assigneeId = match?.uid || normalizedLead.assigneeId || "";
          } else if (field.type === "group") {
            acc.countryCode = normalizedLead.countryCode || "";
            acc.phoneNumber = normalizedLead.phoneNumber || "";
            acc.country = normalizedLead.country || "";
          } else {
            acc[fieldName] = val;
          }

          return acc;
        },
        {} as Record<string, any>,
      );

      setEditableLead({ ...normalizedLead, ...mapped });
      setInitialLead({ ...normalizedLead, ...mapped });

      // ✅ Set uploaded result URL directly from lead data
      setUploadedResultURL(lead.uploadedResult || null);
    }
  }, [lead, fields, isDetailsEditing]);

  useEffect(() => {
    if (!editableLead.country) return;

    const selected = countryCodeList.find(
      (c) => c.countryName === editableLead.country,
    );

    if (selected && editableLead.countryCode !== `+${selected.countryCode}`) {
      setEditableLead((prev) => ({
        ...prev,
        countryCode: `+${selected.countryCode}`,
      }));
    }
  }, [editableLead.country]);

  const isCurrentUserAssignee = Boolean(
    (editableLead.assignee || lead?.assignee || "") ===
      currentUserDisplayName && currentUserDisplayName,
  );

  const isSalesUser = userRole === "sales" || userRole === "part-time sales";

  const isBranchManager = userRole === "Branch Manager";

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    if (name === "qualification") {
      const estimatedOptions = getEstimatedResultOptions(value);
      const studentResultOptions = getStudentResultOptions(value);
      setFields((prev) =>
        prev.map((field) =>
          field.name === "estimatedResult"
            ? { ...field, options: estimatedOptions }
            : field.name === "studentResult"
              ? { ...field, options: studentResultOptions }
              : field,
        ),
      );
      setEditableLead((prev) => ({
        ...prev,
        qualification: value,
        studentResult: studentResultOptions.includes(prev.studentResult)
          ? prev.studentResult
          : "",
        estimatedResult: estimatedOptions.includes(prev.estimatedResult)
          ? prev.estimatedResult
          : "",
      }));
      return;
    }

    setEditableLead((prev) => ({ ...prev, [name]: value }));
  };

  // Helper function to extract filename from Firebase Storage URL
  const extractFilename = (url: string): string => {
    try {
      // Extract the path from the URL
      const urlObj = new URL(url);
      const pathname = decodeURIComponent(urlObj.pathname);

      // Get the filename from the path (after the last /)
      const parts = pathname.split("/");
      const fullFilename = parts[parts.length - 1];

      // Remove the timestamp prefix (format: timestamp_originalname.pdf)
      const underscoreIndex = fullFilename.indexOf("_");
      if (underscoreIndex !== -1) {
        return fullFilename.substring(underscoreIndex + 1);
      }

      return fullFilename;
    } catch (error) {
      console.error("Error extracting filename:", error);
      return "View PDF";
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (file.type !== "application/pdf") {
        setErrors((prev) => ({
          ...prev,
          uploadedResult: "Only PDF files are allowed",
        }));
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          uploadedResult: "File size must be less than 5MB",
        }));
        return;
      }
      setSelectedFile(file);
      setSelectedFileName(file.name);
      setErrors((prev) => ({ ...prev, uploadedResult: "" }));
    } else {
      setSelectedFile(null);
      setSelectedFileName("");
    }
  };

  const validateFields = () => {
    const newErrors: Record<string, string> = {};
    const name = (editableLead.name || "").trim();
    if (!/^[A-Za-z]+( [A-Za-z]+)*$/.test(name)) {
      newErrors.name =
        "Name must only contain letters and single spaces between words.";
    }

    const email = (editableLead.email || "").trim();
    if (email) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(email)) {
        newErrors.email = "Invalid email address.";
      }
    }

    const ic = (editableLead.ic || "").trim();
    if (ic) {
      const icRegex = /^[A-Za-z0-9]{12}$/;
      if (!icRegex.test(ic)) {
        newErrors.ic =
          "IC must contain exactly 12 digits with no symbols or letters";
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.getElementById(`field-${firstErrorField}`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleRemarksSave = async () => {
    try {
      if (!leadId) throw new Error("Lead ID is missing");

      const leadRef = doc(db, "leads", leadId);
      const existingSnap = await getDoc(leadRef);
      const existingData = existingSnap.data() || {};

      const updatedRemarks = (editableLead.remarks || "").trim();

      const updatedFields: Record<string, any> = {
        remarks: updatedRemarks,
        ...getTouchedDateUpdates(existingData),
      };

      await updateDoc(leadRef, updatedFields);

      showSuccessToast("Remarks updated successfully");
      setisRemarkEditing(false);
      if (onSave)
        onSave({
          ...lead,
          ...updatedFields,
          ID: leadId,
          id: leadId,
        });
    } catch (err: any) {
      console.error("Failed to update remarks:", err);
      showErrorToast(err.message || "Failed to save remarks.");
    }
  };

  const handleSave = async () => {
    if (!validateFields()) return;

    try {
      if (!leadId) throw new Error("Lead ID is missing");

      const leadRef = doc(db, "leads", leadId);
      const existingSnap = await getDoc(leadRef);
      const existingData = existingSnap.data() || {};

      const updatedLead = fields.reduce(
        (acc, field) => {
          const key = field.name;

          if (field.type === "file") {
            return acc;
          }

          if (field.type === "group") {
            acc.country = editableLead.country;
            acc.countryCode = editableLead.countryCode;
            acc.phoneNumber = editableLead.phoneNumber;
            return acc;
          }

          let val = editableLead[key];

          if (isSalesUser && (key === "assignee" || key === "assigneeId")) {
            acc[key] = existingData[key] || "";
            return acc;
          }

          if (key === "createdDate") {
            acc[key] = existingData[key] || val;
            return acc;
          }

          if (key === "firstTouchedDate" || key === "lastTouchedDate") {
            const existingTouchedDate = toIsoTouchedDate(existingData[key]);
            if (existingTouchedDate) acc[key] = existingTouchedDate;
          } else if (key === "status" || key === "familyFinancials") {
            acc[key] = val?.toString().trim() || "";
          } else {
            const finalVal = val !== undefined ? val : existingData[key];
            if (finalVal !== undefined) {
              acc[key] = finalVal;
            }
          }

          return acc;
        },
        {} as Record<string, any>,
      );

      updatedLead.branch = getBranchForAssignee(updatedLead.assignee);

      if (!isSalesUser) {
        updatedLead.assigneeId =
          editableLead.assigneeId ?? existingData.assigneeId ?? "";
      }

      if (existingData.SpmTuition || editableLead.SpmTuition) {
        updatedLead.SpmTuition = {
          ...(existingData.SpmTuition || {}),
          ...(editableLead.SpmTuition || {}),
          subject: Array.isArray(editableLead.SpmTuition?.subject)
            ? editableLead.SpmTuition.subject
            : [],
        };
      }

      // UEC-specific fields that are mergeable but are not part of getLeadFields().
      updatedLead.discoverySource =
        editableLead.discoverySource ?? existingData.discoverySource ?? "";

      // Handle PDF file upload if a new file is selected
      if (selectedFile) {
        try {
          // Delete old PDF from storage if it exists
          if (existingData.uploadedResult) {
            try {
              const oldFileRef = ref(storage, existingData.uploadedResult);
              await deleteObject(oldFileRef);
            } catch (deleteError) {
              console.error("Failed to delete old PDF:", deleteError);
              // Continue with upload even if delete fails
            }
          }

          // Upload new PDF
          const timestamp = Date.now();
          const fileName = `${timestamp}_${selectedFile.name}`;
          const storagePath = `leads/${fileName}`;
          const storageRef = ref(storage, storagePath);
          const uploadTask = uploadBytesResumable(storageRef, selectedFile);

          await new Promise<void>((resolve, reject) => {
            uploadTask.on(
              "state_changed",
              null,
              (error) => {
                console.error("Upload error:", error);
                reject(error);
              },
              async () => {
                const downloadURL = await getDownloadURL(
                  uploadTask.snapshot.ref,
                );
                updatedLead.uploadedResult = downloadURL;
                resolve();
              },
            );
          });
        } catch (uploadError: any) {
          console.error("File upload failed:", uploadError);
          showErrorToast("Failed to upload PDF. Please try again.");
          return;
        }
      }

      Object.assign(updatedLead, getTouchedDateUpdates(existingData));

      await updateDoc(leadRef, updatedLead);

      showSuccessToast("Lead updated successfully");
      if (onSave) onSave({ ...updatedLead, ID: leadId, id: leadId });
      setisDetailsEditing(false);
      onClose();
    } catch (err: any) {
      console.error("Failed to update lead:", err);
      showErrorToast(err.message || "Failed to save changes.");
    }
  };

  const careerRecommendations = getCareerRecommendations(
    editableLead.careerTestResult,
  );
  const hasUecRevisionClass =
    editableLead.campaign === "UEC Ultimate Revision Class 2026" ||
    editableLead.tuition?.qualification === "UEC" ||
    toListItems(editableLead.tuition?.subject).length > 0 ||
    editableLead.chineseName ||
    editableLead.identity ||
    editableLead.discoverySource ||
    editableLead.accountStatus;

  return (
    <div className="flex flex-col border-l border-gray-500 w-[500px] bg-white min-h-screen">
      <div className="pb-4">
        {/* Assignee Header */}
        <div className="fixed top-0 right-0 w-[500px] py-2 px-4 flex justify-between items-center bg-blue-500 text-white z-10">
          <h3 className="text-lg font-bold">
            {editableLead.assignee || "Unassigned"}
          </h3>
          <X onClick={onClose} className="w-6 h-6 cursor-pointer" />
        </div>

        {/* Student Info */}
        <div className="px-4 pt-20">
          <div className="flex justify-between items-center">
            <h4 className="text-xl font-bold">
              {editableLead.name || "Student Name"}
            </h4>
            <div className="mt-1">
              <StatusBadge status={editableLead.status || "New"} size="lg" />
            </div>
          </div>
          <div className="mt-2 text-sm space-y-1">
            <div className="flex gap-2">
              <p className="font-semibold">Phone Number: </p>
              <p>
                {formatPhone(
                  editableLead.countryCode,
                  editableLead.phoneNumber,
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <p className="font-semibold">Last Touched: </p>
              <p>
                {convertToDisplayDate(
                  String(editableLead.lastTouchedDate || ""),
                ) || "-"}
              </p>
            </div>
            <div className="flex gap-2">
              <p className="font-semibold">Source Detail: </p>
              <p>{editableLead.sourceDetail || "-"}</p>
            </div>
            <div className="flex gap-2">
              <p className="font-semibold">Interested Courses: </p>
              <p>
                {editableLead.interestedCourses ||
                  (
                    editableLead.courseFinderResult?.answers
                      ?.courseFinderInterests ||
                    editableLead.courseFinderInterests ||
                    []
                  ).join(", ") ||
                  "-"}
              </p>
            </div>
            <div className="flex gap-2">
              <p className="font-semibold">School Name: </p>
              <p>{editableLead.schoolName || "-"}</p>
            </div>
            <div className="flex gap-2 items-center">
              <p className="font-semibold">Uploaded Result:</p>
              {uploadedResultURL ? (
                <div className="flex gap-4">
                  <button
                    onClick={() => window.open(uploadedResultURL, "_blank")}
                    className="py-1 px-3 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    {extractFilename(uploadedResultURL)}
                  </button>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No result uploaded.</p>
              )}
            </div>
            <div className="flex gap-2 items-center">
              <p className="font-semibold">Student Portal Status:</p>
              <p>{editableLead.accountStatus || "-"}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-2 text-xs">
            {[
              "familyFinancials",
              "studentResult",
              "estimatedResult",
              "leadSource",
              "qualification",
            ]
              .filter(
                (key) => editableLead[key] && editableLead[key].trim() !== "",
              )
              .map((key) => (
                <span
                  key={key}
                  className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 font-medium text-blue-700 ring-1 ring-blue-700/10 ring-inset"
                >
                  {editableLead[key] || key}
                </span>
              ))}
          </div>

          <SubmissionHistorySection
            submissions={submissionHistory}
            isLoading={isSubmissionHistoryLoading}
            error={submissionHistoryError}
            onExpandedChange={setShowSubmissionHistory}
            renderSubmission={(submission, position) => (
              <SubmissionHistoryCard
                key={submission.id}
                submission={submission}
                position={position}
              />
            )}
          />

          <div className="mt-4 space-y-3 text-sm">
            <DuplicateLeadDifferencesSection
              currentLead={initialLead}
              duplicateLeads={duplicateLeads}
              isLoading={isDuplicateLeadsLoading}
              error={duplicateLeadsError}
              getDifferences={getLeadDifferences}
              formatFieldName={formatFieldName}
              formatValue={formatAuditValue}
              canUseDuplicateValue={canMergeField}
              onUseDuplicateValue={useDuplicateValue}
              onExpandedChange={setShowDuplicateDifferences}
            />

            <CourseFinderSection lead={editableLead} />

            <CareerTestSection lead={editableLead} />

            <UecRevisionClassSection
              lead={editableLead}
              onSubjectsSaved={(subjects) => {
                const applySubjects = (previous: Record<string, any>) => ({
                  ...previous,
                  tuition: {
                    ...(previous.tuition || {}),
                    subject: subjects,
                  },
                });
                setEditableLead(applySubjects);
                setInitialLead(applySubjects);
                onSave?.(applySubjects(editableLead));
              }}
            />

            <SpmRevisionClassSection
              lead={editableLead}
              onSubjectsSaved={(subjects) => {
                const applySubjects = (previous: Record<string, any>) => ({
                  ...previous,
                  SpmTuition: {
                    ...(previous.SpmTuition || {}),
                    subject: subjects,
                  },
                });
                setEditableLead(applySubjects);
                setInitialLead(applySubjects);
                onSave?.(applySubjects(editableLead));
              }}
            />

            {editableLead.possibleMatches?.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="font-bold text-amber-800">
                  Possible Duplicate Matches
                </p>
                <div className="mt-2 space-y-1 text-amber-900">
                  {editableLead.possibleMatches.map(
                    (match: any, index: number) => (
                      <p key={`possible-match-${index}`}>
                        Possible duplicate by{" "}
                        {(match.matchedFields || [])
                          .map((field: string) =>
                            formatMatchField(field, editableLead),
                          )
                          .join(", ")}
                      </p>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-5 px-4 pt-4">
          <div className="relative group inline-block">
            <div
              className={`p-2 rounded-full ${
                editableLead.overseas === "Yes" ? "bg-[#25EBAC]" : "bg-gray-300"
              }`}
            >
              <Plane className="w-6 h-6 " strokeWidth={1} />
            </div>
            <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-max px-2 py-1 text-sm text-white bg-black rounded opacity-0 group-hover:opacity-100 transition-opacity z-10">
              Overseas
            </div>
          </div>
          <div className="relative group inline-block">
            <div
              className={`p-2 rounded-full ${
                editableLead.videoCall === "Yes"
                  ? "bg-[#25EBAC]"
                  : "bg-gray-300"
              }`}
            >
              <Laptop className="w-6 h-6" strokeWidth={1} />
              <div className="absolute left-1/2 -translate-x-1/2 mt-4 w-max px-2 py-1 text-sm text-white bg-black rounded opacity-0 group-hover:opacity-100 transition-opacity z-10">
                Video Call
              </div>
            </div>
          </div>
          <div className="relative group inline-block">
            <div
              className={`p-2 rounded-full ${
                editableLead.campusVisit === "Yes"
                  ? "bg-[#25EBAC]"
                  : "bg-gray-300"
              }`}
            >
              <School className="w-6 h-6" strokeWidth={1} />
              <div className="absolute left-1/2 -translate-x-1/2 mt-4 w-max px-2 py-1 text-sm text-white bg-black rounded opacity-0 group-hover:opacity-100 transition-opacity z-10">
                Campus Visit
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Remarks */}
      <div className="px-4 flex flex-col gap-2 border-t border-t-gray-500">
        <div className="flex flex-row justify-between items-center py-4">
          <button
            onClick={() => setShowRemarks(!showRemarks)}
            className="flex gap-2"
          >
            <ChevronDown
              className={`transition-transform duration-200 ${
                showRemarks ? "rotate-180" : ""
              }`}
            />
            <h4 className="font-bold">Remarks</h4>
          </button>
          <button
            className={`text-blue-500 underline ${
              showRemarks && !isRemarkEditing ? "flex" : "hidden"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setisRemarkEditing(true);
            }}
          >
            Edit
          </button>
        </div>
        {showRemarks && (
          <div className="flex flex-col items-center mb-4">
            <textarea
              rows={6}
              name="remarks"
              className={`border border-gray-500 rounded px-1 w-full ${
                !isRemarkEditing ? "bg-gray-200 cursor-not-allowed" : ""
              }`}
              value={editableLead.remarks || ""}
              onChange={handleChange}
              disabled={!isRemarkEditing}
            />
            {isRemarkEditing && (
              <div className="flex gap-4 w-full mt-4">
                <button
                  type="button"
                  onClick={handleRemarksSave}
                  className="w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Save
                </button>
                <button
                  onClick={(event) => cancelEditing(event, setisRemarkEditing)}
                  className="w-full py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Editable Details */}
      <div className="flex flex-col px-4 border-t border-t-gray-500">
        <div className="flex justify-between items-center py-4">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex gap-2"
          >
            <ChevronDown
              className={`transition-transform duration-200 ${
                showDetails ? "rotate-180" : ""
              }`}
            />
            <h4 className="font-bold">Details</h4>
          </button>
          <button
            className={`text-blue-500 underline ${
              showDetails && !isDetailsEditing ? "flex" : "hidden"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setisDetailsEditing(true);
            }}
          >
            Edit
          </button>
        </div>

        {showDetails && (
          <form className="flex flex-col gap-4 pb-10 items-center">
            {fields
              .filter((field) => field.name !== "remarks")
              .map((field) => (
                <div
                  key={field.name}
                  id={`field-${field.name}`}
                  className="flex mx-auto flex-col w-full mb-2"
                >
                  {/* Label + Input */}
                  <div className="flex flex-row items-center">
                    <label className="text-sm w-52">{field.label}: </label>
                    {field.type === "group" ? (
                      <div className="w-full">
                        <GroupField
                          value={{
                            countryCode: editableLead.countryCode || "",
                            phoneNumber: editableLead.phoneNumber || "",
                            country: editableLead.country || "",
                          }}
                          onChange={(updatedObj) => {
                            setEditableLead((prev) => ({
                              ...prev,
                              country: updatedObj.country || prev.country,
                              phoneNumber:
                                updatedObj.phoneNumber || prev.phoneNumber,
                            }));
                          }}
                          groupFields={field.groupField || []}
                          disabled={!isDetailsEditing}
                        />
                      </div>
                    ) : field.type === "select" ? (
                      <select
                        name={field.name}
                        value={
                          field.name === "assignee"
                            ? editableLead.assigneeId || ""
                            : editableLead[field.name] || ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          if (field.name === "assignee" && field.optionsData) {
                            const selected = field.optionsData.find(
                              (o) => o.uid === value,
                            );

                            setEditableLead((prev) => {
                              const prevAssignee = prev.assignee || "";
                              const newAssignee = selected?.displayName || "";

                              const shouldResetStatus =
                                prevAssignee.trim() === "" ||
                                prevAssignee !== newAssignee;

                              return {
                                ...prev,
                                assignee: newAssignee,
                                assigneeId: selected?.uid || "",
                                branch: getBranchForAssignee(newAssignee),
                                status: shouldResetStatus ? "New" : prev.status,
                              };
                            });
                          } else {
                            handleChange(e);
                          }
                        }}
                        disabled={
                          !isDetailsEditing ||
                          (field.name === "assignee" && isSalesUser)
                        }
                        className={`border border-gray-500 rounded p-1 w-full
                          ${
                            field.name === "createdDate" ||
                            (field.name === "assignee" && isSalesUser)
                              ? "bg-gray-200 cursor-not-allowed"
                              : isDetailsEditing
                                ? "bg-white"
                                : "bg-gray-200 cursor-not-allowed"
                          }`}
                      >
                        <option value="">Select</option>
                        {field.optionsData
                          ? field.optionsData.map((opt) => (
                              <option key={opt.uid} value={opt.uid}>
                                {opt.displayName}
                              </option>
                            ))
                          : field.options?.map((opt: string) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                      </select>
                    ) : field.type === "file" ? (
                      <div className="w-full">
                        {/* Custom File Input */}
                        <label
                          className={`flex items-center justify-center w-full border border-gray-600 rounded-md py-2 px-3
                            ${
                              !isDetailsEditing ||
                              (field.name === "assignee" && isSalesUser)
                                ? "bg-gray-200 cursor-not-allowed"
                                : isDetailsEditing
                                  ? "bg-white cursor-pointer"
                                  : "bg-gray-200 cursor-not-allowed"
                            }`}
                        >
                          <span className="text-sm font-medium">
                            {selectedFileName
                              ? "Change File"
                              : "Choose PDF File"}
                          </span>
                          <input
                            id="uploadedResult"
                            name="uploadedResult"
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={handleFileChange}
                            disabled={
                              !isDetailsEditing ||
                              (field.name === "assignee" && isSalesUser)
                            }
                          />
                        </label>

                        {/* Display selected filename or default message */}
                        <p className="text-sm text-gray-600 mt-1 text-center">
                          {selectedFileName ? (
                            <span className="text-blue-600 font-medium">
                              {selectedFileName}
                            </span>
                          ) : uploadedResultURL ? (
                            <span className="text-gray-600">
                              {extractFilename(uploadedResultURL)}
                            </span>
                          ) : (
                            <span className="italic text-gray-400">
                              No file chosen
                            </span>
                          )}
                        </p>
                      </div>
                    ) : (
                      <input
                        type={field.type}
                        name={field.name}
                        value={editableLead[field.name] || ""}
                        onChange={handleChange}
                        disabled={
                          !isDetailsEditing ||
                          (field.name === "assignee" && isSalesUser) ||
                          field.name === "createdDate" ||
                          field.name === "firstTouchedDate" ||
                          field.name === "lastTouchedDate"
                        }
                        className={`border border-gray-500 rounded p-1 w-full ${
                          field.name === "createdDate" ||
                          field.name === "firstTouchedDate" ||
                          field.name === "lastTouchedDate" ||
                          (field.name === "assignee" &&
                            (isSalesUser || isBranchManager))
                            ? "bg-gray-200 cursor-not-allowed"
                            : !isDetailsEditing
                              ? "bg-gray-200 cursor-not-allowed"
                              : ""
                        }`}
                      />
                    )}
                  </div>

                  {errors[field.name] && isDetailsEditing && (
                    <p className="text-xs text-red-600 ml-36 mt-1">
                      {errors[field.name]}
                    </p>
                  )}
                </div>
              ))}

            {isDetailsEditing && (
              <div className="flex gap-4 w-full mt-4">
                <button
                  type="button"
                  onClick={handleSave}
                  className="w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Save
                </button>
                <button
                  onClick={(event) => cancelEditing(event, setisDetailsEditing)}
                  className="w-full py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            )}
          </form>
        )}
      </div>

      <ConfirmationDialog
        isOpen={pendingDuplicateUpdate !== null}
        icon={<AlertTriangle size={32} className="text-amber-500" />}
        title="Use Duplicate Value"
        message={
          pendingDuplicateUpdate
            ? `Update ${formatFieldName(pendingDuplicateUpdate.field)} to "${formatAuditValue(pendingDuplicateUpdate.value)}"? This will save the duplicate value immediately.`
            : ""
        }
        onCancel={() => setPendingDuplicateUpdate(null)}
        onConfirm={() => void confirmDuplicateUpdate()}
      />
    </div>
  );
};

export default UECSideLeadForm;
